import { Box, Flex } from '@chakra-ui/react'
import {
    Button,
    Dropdown,
    Input,
    Player,
    SelectOption,
    useFormContext,
    ValidatedInput
} from '@missionlabs/react'
import { UserCallRoute } from '@missionlabs/types'
import { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { RedirectExternalRadio } from 'shared/components/OutOfHours/RedirectExternalRadio'
import { getTimeoutOptions } from 'shared/components/OutOfHours/timeoutOptions'

import { AddEditTeamFields } from './addEditTeamSchema'

export interface UnansweredOptionsProps {
    mode: 'audioclip' | 'internal' | 'numberE164'
    name?: string
    options?: SelectOption[]
    lineBusy?: boolean
}

export const UnansweredOptions: FC<UnansweredOptionsProps> = ({
    mode,
    name,
    options,
    lineBusy = false
}) => {
    const location = useLocation()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const {
        setValue,
        watch,
        setError,
        clearErrors,
        trigger,
        formState: { errors, isSubmitted }
    } = useFormContext<AddEditTeamFields>()
    const display = watch(`${name}.display` as keyof AddEditTeamFields)

    // A little bit jank as name could be other values, but its constrained to mode === "audioclip" components
    const greetingURL = watch(name as keyof AddEditTeamFields) as string
    const prevGreetingURL = useRef<string | undefined>()

    const handleChange = useCallback(
        (name: keyof AddEditTeamFields, value: string) => {
            // Fields are set programmatically, so re-validate after submit to clear stale errors
            setValue(name, value, { shouldDirty: true, shouldValidate: isSubmitted })
        },
        [isSubmitted, setValue]
    )

    useEffect(() => {
        if (mode === 'audioclip' && name) {
            if (prevGreetingURL.current !== undefined && prevGreetingURL.current !== greetingURL) {
                handleChange(name as keyof AddEditTeamFields, greetingURL)
            }

            prevGreetingURL.current = greetingURL
        }
    }, [greetingURL, handleChange, mode, name])

    const callRoute = useMemo(() => {
        const busyCallRoute = {
            busy: watch('callRoute.busy'),
            busyRedirect: watch('busyRedirect')
        }

        const unansweredCallRoute = {
            inHoursUnanswered: watch('callRoute.inHoursUnanswered'),
            unansweredRedirect: watch('unansweredRedirect'),
            callTimeout: watch('callTimeout')!
        }

        if (name === 'busyRedirect') return busyCallRoute
        if (name === 'unansweredRedirect') return unansweredCallRoute
        return undefined
    }, [name, watch])

    const callRouteType = useMemo(() => {
        if (name === 'busyRedirect') return 'busy'
        if (name === 'unansweredRedirect') return 'inHoursUnanswered'
        return undefined
    }, [name])

    const forwardType = useMemo(() => {
        if (!callRoute || !name) return undefined
        if (name === 'busyRedirect' && 'busy' in callRoute) return callRoute.busy
        if (name === 'unansweredRedirect' && 'inHoursUnanswered' in callRoute) {
            return callRoute.inHoursUnanswered
        }
        return undefined
    }, [callRoute, name])

    const updateValues = useCallback(
        (update: Partial<UserCallRoute>, name?: string) => {
            const { busy, busyRedirect = {}, inHoursUnanswered, unansweredRedirect = {} } = update

            const options = { shouldDirty: true }

            if (name === 'busyRedirect') {
                setValue('callRoute.busy', busy, options)
                setValue('busyRedirect', busyRedirect, options)
            }

            if (name === 'unansweredRedirect') {
                setValue('callRoute.inHoursUnanswered', inHoursUnanswered, options)
                setValue('unansweredRedirect', unansweredRedirect, options)
            }

            // Includes callTimeout, so re-validate the whole form after submit to clear stale errors
            if (isSubmitted) trigger()
        },
        [isSubmitted, setValue, trigger]
    )

    function handleClick(drawer: 'assign' | 'upload') {
        navigate(`${location.pathname}/${drawer}`, { state: name })
    }

    const onNumberValidation = useCallback(
        (isValidNumber: boolean) => {
            const fieldName = name === 'busyRedirect' ? 'busyRedirect' : 'unansweredRedirect'
            if (!isValidNumber) {
                setError(fieldName, { type: 'validate', message: t('validations.number') })
            } else {
                clearErrors(fieldName)
            }
        },
        [clearErrors, name, setError, t]
    )

    return (
        <Flex
            flexWrap="wrap"
            gap="16px"
            align="start"
            pl="24px"
            sx={{ ':not(:last-child)': { pb: '8px' } }}
        >
            {!lineBusy && mode !== 'numberE164' && (
                <ValidatedInput maxW="196px" name="callTimeout" shouldRegister={false}>
                    <Dropdown
                        options={getTimeoutOptions(t)}
                        value={watch('callTimeout')}
                        placeholder={t('Select')}
                        onChange={value => handleChange('callTimeout', value)}
                    />
                </ValidatedInput>
            )}
            {mode === 'audioclip' && (
                <>
                    <ValidatedInput flex={1} name={name} shouldRegister={false}>
                        <Dropdown
                            options={options}
                            value={greetingURL}
                            onChange={value => handleChange(name as keyof AddEditTeamFields, value)}
                        />
                    </ValidatedInput>
                    <Button variant="creationary" onClick={() => handleClick('upload')}>
                        {t('admin.teams.form.uploadNew')}
                    </Button>
                    {!!greetingURL && (
                        <Box w="100%" alignItems="flex-start" id="voicemail-player">
                            <Player url={greetingURL} width="100%" />
                        </Box>
                    )}
                </>
            )}
            {mode === 'internal' && (
                <>
                    {!display ? (
                        <Button w="196px" variant="primary" onClick={() => handleClick('assign')}>
                            {t('calls.selectUTM')}
                        </Button>
                    ) : (
                        <ValidatedInput
                            name={`${name}.display`}
                            onClick={() => handleClick('assign')}
                        >
                            <Input isReadOnly cursor="pointer" value={display as string} />
                        </ValidatedInput>
                    )}
                </>
            )}
            {mode === 'numberE164' && (
                // <ValidatedInput name={`${name}.numberE164`} maxW="60%">
                //     <Input placeholder={t('Enter number')} />
                // </ValidatedInput>
                <RedirectExternalRadio
                    hideRadio
                    presentAlways={name === 'unansweredRedirect'}
                    type={callRouteType!}
                    callRoute={callRoute!}
                    forwardType={forwardType!}
                    onUpdate={value => updateValues(value, name)}
                    onNumberValidation={onNumberValidation}
                    callTimeoutError={
                        name === 'unansweredRedirect' ? errors.callTimeout : undefined
                    }
                />
            )}
        </Flex>
    )
}
